import Link from 'next/link';
import { FiChevronRight, FiHome } from 'react-icons/fi';
import { generateBreadcrumbSchema } from '@/utils/schemaGenerator';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  variant?: 'light' | 'dark';
  className?: string;
}

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://astroji.com';

export default function Breadcrumbs({
  items,
  variant = 'light',
  className = '',
}: BreadcrumbsProps) {
  if (!items || items.length === 0) return null;

  // Prepend Home if missing
  const fullItems: BreadcrumbItem[] = items[0]?.label.toLowerCase() === 'home'
    ? items
    : [{ label: 'Home', href: '/' }, ...items];

  // Generate Schema.org items
  const schemaItems = fullItems.map((item) => {
    const rawHref = item.href || '';
    const fullUrl = rawHref.startsWith('http')
      ? rawHref
      : `${SITE_URL}${rawHref.startsWith('/') ? rawHref : `/${rawHref}`}`;
    return {
      name: item.label,
      item: fullUrl,
    };
  });

  const breadcrumbSchema = generateBreadcrumbSchema(schemaItems);

  // Variant color definitions
  const isDark = variant === 'dark';
  const navTextColor = isDark ? 'text-amber-200/90' : 'text-amber-900/70';
  const chevronColor = isDark ? 'text-amber-400/60' : 'text-amber-600/40';
  const activeTextColor = isDark ? 'text-amber-100 font-semibold' : 'text-amber-950 font-semibold';
  const linkHoverColor = isDark ? 'hover:text-amber-100 text-amber-200/90' : 'hover:text-amber-700 text-amber-900/70';
  const homeIconColor = isDark ? 'text-amber-300' : 'text-amber-700/70';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <nav
        aria-label="Breadcrumb"
        className={`flex items-center text-xs md:text-sm ${navTextColor} py-2.5 px-1 overflow-x-auto no-scrollbar ${className}`}
      >
        <ol className="flex items-center space-x-1.5 md:space-x-2 whitespace-nowrap">
          {fullItems.map((item, index) => {
            const isLast = index === fullItems.length - 1;
            const isHome = index === 0;

            return (
              <li key={index} className="inline-flex items-center">
                {index > 0 && (
                  <FiChevronRight className={`w-3.5 h-3.5 mx-1 ${chevronColor} flex-shrink-0`} />
                )}
                {isLast || !item.href ? (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={`${activeTextColor} truncate max-w-[200px] sm:max-w-none`}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    href={item.href}
                    className={`inline-flex items-center transition-colors font-medium ${linkHoverColor}`}
                  >
                    {isHome && <FiHome className={`w-3.5 h-3.5 mr-1 ${homeIconColor}`} />}
                    <span>{item.label}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
